import { nextYear, thisYear } from "../../../Footer/footer";
import { buildRegistrationData } from "../pdf/buildRegistrationData";

const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const encoder = new TextEncoder();

const escapeXml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const run = (text, { bold = false, color, size = 18 } = {}) => {
  const properties = [
    bold ? "<w:b/>" : "",
    color ? `<w:color w:val="${color}"/>` : "",
    `<w:sz w:val="${size}"/><w:szCs w:val="${size}"/>`,
  ].join("");

  return `<w:r><w:rPr>${properties}</w:rPr><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;
};

const paragraph = (
  content = "",
  { alignment, bold = false, color, size, spacingAfter = 100 } = {}
) => {
  const runs = Array.isArray(content)
    ? content
        .map((item) =>
          typeof item === "string" ? run(item, { bold, color, size }) : run(item.text, item)
        )
        .join("")
    : run(content, { bold, color, size });

  return `<w:p><w:pPr>${alignment ? `<w:jc w:val="${alignment}"/>` : ""}<w:spacing w:after="${spacingAfter}"/></w:pPr>${runs}</w:p>`;
};

const multilineParagraph = (value = "", options = {}) => {
  const lines = String(value).split(/\r?\n/);
  const runs = lines
    .map((line, index) => `${index ? "<w:r><w:br/></w:r>" : ""}${run(line, options)}`)
    .join("");

  return `<w:p><w:pPr><w:spacing w:after="100"/></w:pPr>${runs}</w:p>`;
};

const cell = (
  content,
  { width, shading, bold = false, verticalAlign = "center", borders } = {}
) => {
  const cellContent = Array.isArray(content)
    ? content.join("")
    : paragraph(content, { bold, spacingAfter: 0 });

  return `<w:tc><w:tcPr>${width ? `<w:tcW w:w="${width}" w:type="dxa"/>` : ""}${
    shading ? `<w:shd w:fill="${shading}"/>` : ""
  }${verticalAlign ? `<w:vAlign w:val="${verticalAlign}"/>` : ""}${
    borders === false
      ? '<w:tcBorders><w:top w:val="nil"/><w:left w:val="nil"/><w:bottom w:val="nil"/><w:right w:val="nil"/></w:tcBorders>'
      : ""
  }</w:tcPr>${cellContent}</w:tc>`;
};

const row = (cells) => `<w:tr>${cells.join("")}</w:tr>`;

const table = (rows, widths, { borders = true } = {}) => `
  <w:tbl>
    <w:tblPr>
      <w:tblW w:w="0" w:type="auto"/>
      <w:tblLayout w:type="fixed"/>
      <w:tblBorders>
        ${["top", "left", "bottom", "right", "insideH", "insideV"]
          .map((side) => `<w:${side} w:val="${borders ? "single" : "nil"}" w:sz="4" w:color="D9DEE3"/>`)
          .join("")}
      </w:tblBorders>
    </w:tblPr>
    <w:tblGrid>${widths.map((width) => `<w:gridCol w:w="${width}"/>`).join("")}</w:tblGrid>
    ${rows.join("")}
  </w:tbl>`;

const buildDocumentXml = ({
  formData,
  priceSummaryUI,
  originalTotal,
  discountedTotal,
  voucherValid,
  formatCities,
}) => {
  const personalRows = [
    ["Full Name", formData.fullName],
    ["Institution", formData.institution],
    ["Official Representative", formData.officialRepresentative],
    ["Email", formData.email],
    ["Phone", formData.phone],
  ];

  const paymentRows = [
    ["Bank", "OCBC NISP, BRANCH TEUKU UMAR DENPASAR BALI"],
    ["Account Name", "CV Naresy Indonesia"],
    ["Account Number", "160800014310"],
    ["Swift Code", "NISPIDJA"],
    ["Branch Code", "07160"],
    ["Bank Code", "028"],
  ];

  const priceRows = priceSummaryUI.map((detail, index) => {
    const formattedCities = formatCities(detail.cities);
    const details = [
      paragraph(detail.scheduleName, { bold: true, spacingAfter: 0 }),
      paragraph(
        `${detail.packageType} Package - AUD ${detail.price.toLocaleString()} for ${detail.cityCount}`,
        { spacingAfter: formattedCities ? 0 : 40 }
      ),
      formattedCities ? paragraph(formattedCities, { size: 16, spacingAfter: 40 }) : "",
    ];

    return row([cell(details, { width: 9026, shading: index % 2 ? "F8F9FA" : undefined })]);
  });

  const totalText = voucherValid
    ? [
        { text: `Original Total: AUD ${originalTotal.toLocaleString()}`, bold: true },
        { text: ` | Discounted Total: AUD ${discountedTotal.toLocaleString()}`, bold: true },
      ]
    : [{ text: `Total: AUD ${discountedTotal.toLocaleString()}`, bold: true }];

  const signatureTable = table(
    [
      row([
        cell(
          [
            paragraph("Place and Date:", { bold: true, spacingAfter: 240 }),
            paragraph("________________________________", { spacingAfter: 0 }),
          ],
          { width: 4513, borders: false, verticalAlign: "top" }
        ),
        cell(
          [
            paragraph("Signature:", { alignment: "center", bold: true, spacingAfter: 700 }),
            paragraph("________________________________", { alignment: "center", spacingAfter: 40 }),
            paragraph(formData.fullName || "", {
              alignment: "center",
              bold: true,
              spacingAfter: 0,
            }),
            paragraph(formData.institution || "", { alignment: "center", spacingAfter: 0 }),
          ],
          { width: 4513, borders: false, verticalAlign: "top" }
        ),
      ]),
    ],
    [4513, 4513],
    { borders: false }
  );

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${paragraph(`NIEC WORLD EDUCATION FESTIVAL ${thisYear} - ${nextYear}`, {
      alignment: "center",
      bold: true,
      color: "DC3545",
      size: 26,
      spacingAfter: 80,
    })}
    ${paragraph("Registration Summary & Commitment Statement", {
      alignment: "center",
      bold: true,
      size: 22,
      spacingAfter: 220,
    })}
    ${paragraph(
      "For the university/institution who gets 50% off, there will be no accommodation provided",
      { color: "DC3545", spacingAfter: 160 }
    )}
    ${paragraph("Personal Information", { bold: true, size: 20, spacingAfter: 60 })}
    ${table(
      [
        row([
          cell("Field", { width: 3159, shading: "F8F9FA", bold: true }),
          cell("Value", { width: 5867, shading: "F8F9FA", bold: true }),
        ]),
        ...personalRows.map(([label, value], index) =>
          row([
            cell(label, { width: 3159, shading: index % 2 ? "F8F9FA" : undefined }),
            cell(value || "", { width: 5867, shading: index % 2 ? "F8F9FA" : undefined }),
          ])
        ),
      ],
      [3159, 5867]
    )}
    ${paragraph("Price Summary", { bold: true, size: 20, spacingAfter: 60 })}
    ${table(
      [
        row([cell("Price Details", { width: 9026, shading: "F8F9FA", bold: true })]),
        ...priceRows,
        row([cell([paragraph(totalText, { spacingAfter: 0 })], { width: 9026, shading: "F8F9FA" })]),
      ],
      [9026]
    )}
    ${paragraph("Payment Information", { bold: true, size: 20, spacingAfter: 60 })}
    ${table(
      [
        row([
          cell("Field", { width: 2708, shading: "F8F9FA", bold: true }),
          cell("Details", { width: 6318, shading: "F8F9FA", bold: true }),
        ]),
        ...paymentRows.map(([label, value], index) =>
          row([
            cell(label, { width: 2708, shading: index % 2 ? "F8F9FA" : undefined }),
            cell(value, { width: 6318, shading: index % 2 ? "F8F9FA" : undefined }),
          ])
        ),
      ],
      [2708, 6318]
    )}
    ${paragraph("Notes:", { bold: true, spacingAfter: 40 })}
    ${table(
      [
        row([
          cell([multilineParagraph(formData.notes || "")], {
            width: 9026,
            shading: "F8F9FA",
            verticalAlign: "top",
          }),
        ]),
      ],
      [9026]
    )}
    ${paragraph("", { spacingAfter: 180 })}
    ${signatureTable}
    ${paragraph(
      `This is an official document from your NIEC World Education Festival ${thisYear} - ${nextYear} registration. Generated on: ${new Date().toLocaleDateString()}`,
      { alignment: "center", size: 16, spacingAfter: 0 }
    )}
    <w:sectPr>
      <w:pgSz w:w="11906" w:h="16838"/>
      <w:pgMar w:top="720" w:right="720" w:bottom="720" w:left="720" w:header="360" w:footer="360" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`;
};

const crcTable = Array.from({ length: 256 }, (_, index) => {
  let value = index;
  for (let bit = 0; bit < 8; bit += 1) {
    value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
  }
  return value >>> 0;
});

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
};

const uint16 = (value) => {
  const bytes = new Uint8Array(2);
  new DataView(bytes.buffer).setUint16(0, value, true);
  return bytes;
};

const uint32 = (value) => {
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, value, true);
  return bytes;
};

const combineBytes = (parts) => {
  const size = parts.reduce((total, part) => total + part.length, 0);
  const result = new Uint8Array(size);
  let offset = 0;
  parts.forEach((part) => {
    result.set(part, offset);
    offset += part.length;
  });
  return result;
};

const createZip = (files) => {
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach(({ name, content }) => {
    const nameBytes = encoder.encode(name);
    const contentBytes = encoder.encode(content);
    const checksum = crc32(contentBytes);
    const localHeader = combineBytes([
      uint32(0x04034b50),
      uint16(20),
      uint16(0x0800),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(checksum),
      uint32(contentBytes.length),
      uint32(contentBytes.length),
      uint16(nameBytes.length),
      uint16(0),
      nameBytes,
    ]);

    localParts.push(localHeader, contentBytes);
    centralParts.push(
      combineBytes([
        uint32(0x02014b50),
        uint16(20),
        uint16(20),
        uint16(0x0800),
        uint16(0),
        uint16(0),
        uint16(0),
        uint32(checksum),
        uint32(contentBytes.length),
        uint32(contentBytes.length),
        uint16(nameBytes.length),
        uint16(0),
        uint16(0),
        uint16(0),
        uint16(0),
        uint32(0),
        uint32(offset),
        nameBytes,
      ])
    );

    offset += localHeader.length + contentBytes.length;
  });

  const centralDirectory = combineBytes(centralParts);
  const endRecord = combineBytes([
    uint32(0x06054b50),
    uint16(0),
    uint16(0),
    uint16(files.length),
    uint16(files.length),
    uint32(centralDirectory.length),
    uint32(offset),
    uint16(0),
  ]);

  return combineBytes([...localParts, centralDirectory, endRecord]);
};

const createDocxBlob = (documentXml) => {
  const files = [
    {
      name: "[Content_Types].xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`,
    },
    {
      name: "_rels/.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
    },
    { name: "word/document.xml", content: documentXml },
  ];

  return new Blob([createZip(files)], { type: DOCX_MIME_TYPE });
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const generateRegistrationDocx = ({
  methods,
  voucherValid,
  calculateSchedulePricing,
  formatCities,
}) => {
  const data = buildRegistrationData({
    methods,
    voucherValid,
    calculateSchedulePricing,
  });
  const documentXml = buildDocumentXml({
    ...data,
    voucherValid,
    formatCities,
  });
  const fileName = `NIEC_Registration_${data.formData.fullName || "Document"}.docx`;

  downloadBlob(createDocxBlob(documentXml), fileName);
};
